import React, { memo, useContext } from 'react'
import { ThemeContext } from 'styled-components'
import * as Styled from './C006.styles'
import { C006Props } from './types'
import { agnostifyUrl } from '../../../helpers/utils'
import { createGolNonContentDataAttributes } from '../../../helpers/tracking'

/**
 * - C006 is a heading component which is used by authors to render H2s, with or without a link.
 * - The heading can be horizontally aligned based on the textAlignment prop.
 * */

const C006 = ({
  link = {
    url: '',
    title: '',
    target: '',
    copy: ''
  },
  backgroundColor,
  hasMargin = false,
  heading,
  linkColor,
  onLinkClick,
  textColor,
  textAlignment = 'left'
}: C006Props): React.ReactElement => {
  const theme = useContext(ThemeContext)
  const isSainsburys = theme?.channel === 'sainsburys'
  const hasLink = !!link?.url

  const addGolTracking = (event) => {
    const dataAttributes = createGolNonContentDataAttributes(
      'link',
      'click',
      'nav link',
      `C006-${heading?.toLowerCase() ?? 'NA'}-${link.copy?.toLowerCase()}`,
      'navigation'
    )
    Object.keys(dataAttributes).forEach((attribute) =>
      event.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event) => {
    if (onLinkClick) {
      onLinkClick(event, agnostifyUrl(link?.url))
    }
    addGolTracking(event)
  }

  return (
    <header data-test='c006-container-wrapper'>
      <Styled.Container
        testid='c006-container'
        backgroundColor={backgroundColor}
        hasMargin={hasMargin}
        gap='none'
        cols={{ sm: 1, md: hasLink ? 2 : 1 }}
      >
        <Styled.Heading
          data-test='c006-heading'
          textColor={textColor}
          textAlignment={textAlignment}
          className={isSainsburys ? 'ln-u-display-2' : undefined} // this is to mitigate the large font sizes on Luna, should be reverted after they've addressed
        >
          {heading}
        </Styled.Heading>
        {link?.url && link?.copy && (
          <Styled.LinkContainer linkColor={linkColor}>
            <a
              data-test='c006-cta'
              target={link?.target}
              title={link?.title}
              href={agnostifyUrl(link?.url)}
              onClick={(event) => ctaClick(event)}
            >
              {link?.copy}
              <Styled.Chevron data-test='c006-cta-chevron' dir='right' linkColor={linkColor} />
            </a>
          </Styled.LinkContainer>
        )}
      </Styled.Container>
    </header>
  )
}

export default memo(C006)
